import React from 'react';
import QuizIcon from '@mui/icons-material/Quiz';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArticleIcon from '@mui/icons-material/Article';
import SchoolIcon from '@mui/icons-material/School';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ChecklistIcon from '@mui/icons-material/Checklist';

export type Conversation = {
  _id: string;
  title: string;
  settings?: { gradeLevel?: string; subject?: string; standardIds?: string[] };
};

export type Attachment = {
  id: string;
  type: 'image' | 'document';
  filename: string;
  fileType: string;
  fileSize?: number;
  data: string; // base64 data URL for images for now
};

export type Message = { role: 'user' | 'assistant'; content: string; meta?: { model?: string; latency_ms?: number; contentType?: string; showButtons?: boolean }; attachments?: Attachment[] };

export interface CodeProps extends React.HTMLAttributes<HTMLElement> {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

// Helper function to detect and parse ASCII tables from code blocks
export const parseAsciiTable = (text: string): { isTable: boolean; headers: string[]; rows: string[][] } => {
  const lines = text.trim().split('\n').filter(line => line.trim());
  if (lines.length < 2) return { isTable: false, headers: [], rows: [] };
  
  // Check if lines contain pipe characters (table-like format)
  const pipeLines = lines.filter(line => line.includes('|'));
  if (pipeLines.length < 2) return { isTable: false, headers: [], rows: [] };
  
  // Parse cells from pipe-separated lines
  const parseLine = (line: string): string[] => {
    return line.split('|')
      .map(cell => cell.trim())
      .filter((_, idx, arr) => idx > 0 && idx < arr.length); // Remove empty first/last from |...|
  };
  
  // Check for separator row (contains only dashes and pipes)
  const separatorIdx = lines.findIndex(line => /^\|?[\s\-:|]+\|?$/.test(line));
  
  if (separatorIdx === -1 || separatorIdx === 0) {
    // No separator found, treat first line as header
    const headers = parseLine(pipeLines[0]);
    const rows = pipeLines.slice(1).map(parseLine).filter(row => row.length > 0);
    if (headers.length > 0 && rows.length > 0) {
      return { isTable: true, headers, rows };
    }
    return { isTable: false, headers: [], rows: [] };
  }
  
  // Standard markdown table with separator
  const headers = parseLine(lines[separatorIdx - 1]);
  const rows = lines.slice(separatorIdx + 1).map(parseLine).filter(row => row.length > 0);
  
  if (headers.length > 0) {
    return { isTable: true, headers, rows };
  }
  
  return { isTable: false, headers: [], rows: [] };
};

export const subjects = ['ELA', 'Math'];

// ==================== SUGGESTION SYSTEM TYPES & HELPERS ====================

// Type for suggestion items (both contextual and universal)
export interface SuggestionItem {
  id: string;
  label: string;
  description?: string; // Tooltip text
  icon?: React.ReactNode;
  priority?: number; // Higher = more relevant
}

// Helper function to get personalization microcopy based on teacher profile and context
export const getPersonalizationText = (
  gradeLevel?: string,
  subject?: string,
  standardCodes?: string[],
  standardsSystem?: string
): string => {
  const parts: string[] = [];
  
  // Format grade level
  if (gradeLevel) {
    const gradeStr = gradeLevel.trim();
    if (/^k$/i.test(gradeStr)) {
      parts.push('Kindergarten');
    } else if (/^pre.?k/i.test(gradeStr)) {
      parts.push('Pre-K');
    } else {
      parts.push(`Grade ${gradeStr}`);
    }
  }
  
  // Add standard code if available (first one as example)
  if (standardCodes && standardCodes.length > 0) {
    const firstStandard = standardCodes[0];
    // Detect standards system from code pattern
    const isTEKS = /^\d+\.\d+/.test(firstStandard) || firstStandard.includes('TEKS');
    const isCCSS = /^[A-Z]+\.\d+/.test(firstStandard) || firstStandard.includes('CCSS');
    
    if (isTEKS) {
      parts.push(`TEKS ${firstStandard}`);
    } else if (isCCSS) {
      parts.push(`CCSS ${firstStandard}`);
    } else if (standardsSystem) {
      parts.push(`${standardsSystem} ${firstStandard}`);
    } else {
      parts.push(firstStandard);
    }
  } else if (subject) {
    // Fallback to subject if no standards
    parts.push(`${subject}`);
  }
  
  // Build final string
  if (parts.length === 0) {
    return 'Uses your profile settings';
  }
  
  return `Aligned to ${parts.join(' • ')}`;
};

// Universal tools that are always available (not context-dependent)
export const UNIVERSAL_TOOLS: SuggestionItem[] = [
  {
    id: 'lesson-45min',
    label: 'Create a 45 min lesson about...',
    description: 'Generates a full lesson plan with objectives, activities, and assessment. Add your topic after clicking.',
    icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
  },
  {
    id: 'mini-lesson',
    label: 'Generate a 20-min mini-lesson about...',
    description: 'Creates a focused mini-lesson for quick instruction. Add your topic after clicking.',
    icon: <SchoolIcon sx={{ fontSize: 18 }} />,
  },
  {
    id: 'worksheet',
    label: 'Create a worksheet about...',
    description: 'Generates a practice worksheet with problems and answer key. Add your topic after clicking.',
    icon: <ArticleIcon sx={{ fontSize: 18 }} />,
  },
  {
    id: 'passage-questions',
    label: 'Create a passage and questions about...',
    description: 'Generates a grade-appropriate reading passage with comprehension questions. Add your topic after clicking.',
    icon: <ArticleIcon sx={{ fontSize: 18 }} />,
  },
  {
    id: 'lesson-3days',
    label: 'Create a 3-day lesson plan about...',
    description: 'Builds a multi-day lesson sequence with clear progression. Add your topic after clicking.',
    icon: <FormatListBulletedIcon sx={{ fontSize: 18 }} />,
  },
  {
    id: 'short-text',
    label: 'Create a short text about...',
    description: 'Creates a grade-appropriate reading passage. Add your topic after clicking.',
    icon: <ArticleIcon sx={{ fontSize: 18 }} />,
  },
];

// Priority scoring for contextual suggestions based on content type
export const getSuggestionPriority = (suggestion: string, lastAssistantContent: string): number => {
  const a = lastAssistantContent.toLowerCase();
  const s = suggestion.toLowerCase();
  
  // Content type detection patterns
  const contentPatterns = {
    passage: /passage|text|reading|story|article|excerpt/.test(a),
    lessonPlan: /lesson plan|objectives|materials|procedures|activities|assessment/.test(a),
    quiz: /quiz|questions?:|multiple choice|short answer|assessment/.test(a),
    worksheet: /worksheet|practice|problems?:|exercises/.test(a),
    rubric: /rubric|criteria|scoring|performance levels/.test(a),
    cer: /claim[- ]?evidence[- ]?reasoning|cer/.test(a),
  };
  
  let priority = 50; // Base priority
  
  // Boost priority for highly relevant follow-ups
  if (contentPatterns.passage) {
    if (s.includes('comprehension') || s.includes('question')) priority += 40;
    if (s.includes('vocabulary') || s.includes('word')) priority += 30;
    if (s.includes('summarize') || s.includes('summary')) priority += 25;
  }
  
  if (contentPatterns.lessonPlan) {
    if (s.includes('exit ticket')) priority += 40;
    if (s.includes('formative') || s.includes('assessment')) priority += 35;
    if (s.includes('differentiat')) priority += 30;
    if (s.includes('handout')) priority += 25;
  }
  
  if (contentPatterns.quiz) {
    if (s.includes('answer key') || s.includes('rationale')) priority += 40;
    if (s.includes('worksheet') || s.includes('practice')) priority += 30;
    if (s.includes('rigor') || s.includes('more question')) priority += 25;
  }
  
  if (contentPatterns.worksheet) {
    if (s.includes('answer key')) priority += 35;
    if (s.includes('word problem') || s.includes('real-life')) priority += 30;
  }
  
  if (contentPatterns.rubric) {
    if (s.includes('checklist') || s.includes('student-facing')) priority += 35;
    if (s.includes('exemplar')) priority += 30;
  }
  
  return priority;
};

// Icon mapping for dynamically generated suggestions
export const getSuggestionIcon = (suggestion: string): React.ReactNode => {
  const s = suggestion.toLowerCase();
  
  if (s.includes('question') || s.includes('quiz') || s.includes('assessment')) {
    return <QuizIcon sx={{ fontSize: 18 }} />;
  }
  if (s.includes('lesson') || s.includes('lesson plan')) {
    return <AssignmentIcon sx={{ fontSize: 18 }} />;
  }
  if (s.includes('text') || s.includes('passage') || s.includes('reading')) {
    return <ArticleIcon sx={{ fontSize: 18 }} />;
  }
  if (s.includes('differentiat') || s.includes('el student') || s.includes('scaffold')) {
    return <TipsAndUpdatesIcon sx={{ fontSize: 18 }} />;
  }
  if (s.includes('exit ticket') || s.includes('warm-up')) {
    return <ChecklistIcon sx={{ fontSize: 18 }} />;
  }
  if (s.includes('rubric') || s.includes('checklist')) {
    return <FormatListBulletedIcon sx={{ fontSize: 18 }} />;
  }
  if (s.includes('problem') || s.includes('practice') || s.includes('worksheet')) {
    return <SchoolIcon sx={{ fontSize: 18 }} />;
  }
  
  // Default icon
  return <LightbulbIcon sx={{ fontSize: 18 }} />;
};

// ==================== END SUGGESTION SYSTEM ====================
